import classNames from "classnames";
import React, { PropsWithChildren } from "react";
import { Control, FieldPath, FieldValues, Controller } from "react-hook-form";
import resolvePath from "Utils/resolvePath";
import { CustomInput, CustomInputProps } from "Components/ReactstrapSlim";

export interface SelectInputProps<T extends FieldValues>
   extends Partial<CustomInputProps> {
   control: Control<T>;
   name: FieldPath<T>;
}

export interface SelectInputFLProps<T extends FieldValues>
   extends SelectInputProps<T> {
   optionalLabel?: string | React.ReactNode;
   mobilePlaceholder?: string;
   inputClassName?: string | undefined;
   onDark?: boolean;
   hasValue?: (v?: unknown) => boolean;
}

export const SelectInput = <T extends FieldValues>({
   name,
   control,
   type,
   ...rest
}: PropsWithChildren<SelectInputProps<T>>) => {
   return (
      <Controller
         name={name}
         control={control}
         render={({ field }) => {
            const { name, value, onChange, onBlur } = field;
            return (
               <CustomInput
                  type="select"
                  id={name}
                  name={name}
                  value={value ?? undefined}
                  onChange={onChange}
                  onBlur={onBlur}
                  {...rest}
               />
            );
         }}
      />
   );
};

export const SelectInputFL = <T extends FieldValues>({
   name,
   control,
   placeholder,
   mobilePlaceholder = "",
   className = "",
   onDark = false,
   optionalLabel,
   type,
   inputClassName,
   defaultValue,
   hasValue = () => true,
   ...rest
}: PropsWithChildren<SelectInputFLProps<T>>) => (
   <Controller
      name={name}
      control={control}
      render={({ field, formState: { errors } }) => {
         const { name, value, onChange, onBlur } = field;
         const error = resolvePath(errors, name);

         const wrapperClasses = classNames(
            "rfl",
            className,
            {
               "rfl--invalid": !!error,
               "rfl--hasvalue": hasValue(value),
               "rfl-on-dark": onDark,
               "rfl--disabled": rest.disabled,
            },
            rest.bsSize ? `rfl-${rest.bsSize}` : null
         );

         return (
            <div className={wrapperClasses}>
               <CustomInput
                  type="select"
                  className={classNames(
                     "rfl-input form-control",
                     inputClassName
                  )}
                  id={name}
                  value={value ?? undefined}
                  onChange={onChange}
                  onBlur={onBlur}
                  {...rest}
               />
               {!error && (
                  <label htmlFor={name} className="rfl-label">
                     {mobilePlaceholder ? (
                        <>
                           <span className="desktop-label">{placeholder}</span>
                           <span className="mobile-label">
                              {mobilePlaceholder}
                           </span>
                        </>
                     ) : (
                        placeholder
                     )}
                  </label>
               )}
               {!error && optionalLabel && (
                  <span className="rfl-optional">{optionalLabel}</span>
               )}
               {!!error && (
                  <div role="alert" className="rfl-valdation-message">
                     {error.message}
                  </div>
               )}
            </div>
         );
      }}
   />
);
